import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Hint, Tag } from '@tecsinapse/cortex-react'
import { FiPlusCircle } from 'react-icons/fi'
import { IoMdClose } from 'react-icons/io'
import { InputBaseVariants } from '@tecsinapse/cortex-core'
import { useController, useFormContext } from 'react-hook-form'
import { Trans } from '@lingui/react/macro'
import { ErrorMessage } from '@hookform/error-message'
import { PhoneInput } from '../../core/components/phone-input'

interface FormPhoneFieldProps extends Record<string, any> {
  labelInput: string
  placeholderInput?: string
  onAddFieldValueValidation?: (value: string) => boolean
}

export const FormPhoneField = ({
  name,
  placeholderInput,
  labelInput,
  labelTags,
  validateField,
  disabled,
  onAddFieldValueValidation
}: FormPhoneFieldProps) => {
  const { setValue, control } = useFormContext()
  const {
    formState: { errors, submitCount },
    field: { value },
    fieldState: { isTouched, error }
  } = useController({ name, control })
  const [inputValue, setInputValue] = useState('')
  const [validationError, setValidationError] = useState(false)

  useEffect(() => {
    if (value?.length > 0) {
      validateField?.()
    }
  }, [value])

  const handleAddFieldValue = useCallback(() => {
    let isValid = true

    if (onAddFieldValueValidation) {
      isValid = onAddFieldValueValidation(inputValue)
    }

    if (isValid) {
      const updatedValues = [...value, inputValue]
      setValue(name, updatedValues, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      })
      setInputValue('')
      setValidationError(false)
      setShowInfoMessage(false)
    } else {
      setValidationError(true)
    }
  }, [value, inputValue, name, onAddFieldValueValidation, setValue])

  const handleRemoveFieldValue = (_value: unknown) => {
    const updatedValues = value.filter(
      (currentValue: unknown) => currentValue !== _value
    )
    setValue(name, updatedValues, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    })
  }

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        handleAddFieldValue()
      }
    },
    [handleAddFieldValue]
  )

  const inputCommonProps = useMemo(
    () => ({
      label: labelInput,
      placeholder: placeholderInput,
      className: 'flex-1',
      variants: {
        intent: validationError ? 'error' : 'default'
      } as InputBaseVariants,
      onKeyDown: handleKeyPress,
      disabled
    }),
    [disabled, handleKeyPress, labelInput, placeholderInput, validationError]
  )
  const [showInfoMessage, setShowInfoMessage] = useState(false)

  return (
    <>
      <div className='flex flex-row space-x-deca items-center'>
        <PhoneInput
          onChange={value => {
            setInputValue(value)
            setValidationError(false)
          }}
          value={inputValue}
          inputProps={{ ...inputCommonProps }}
        />
        <Button
          aria-label={`Adicionar ${labelInput}`}
          disabled={!inputValue.length || disabled}
          type='button'
          variants={{ size: 'square' }}
          onClick={handleAddFieldValue}
          data-testid='add-field-value-button'
        >
          <FiPlusCircle />
        </Button>
      </div>
      {error && (isTouched || submitCount > 0) ? (
        <ErrorMessage
          name={name}
          errors={errors}
          render={({ message }) => (
            <Hint variants={{ intent: 'error' }}>{message}</Hint>
          )}
        />
      ) : null}
      {validationError && (
        <Hint variants={{ intent: 'error' }}>Valor inválido</Hint>
      )}
      {showInfoMessage && !errors[name] && !validationError && (
        <Hint variants={{ intent: 'error' }}>
          <Trans>Clique no botão ao lado para adicionar</Trans>
        </Hint>
      )}

      {value.length > 0 && (
        <p className='font-bold text-sub text-secondary-medium my-mili'>
          {labelTags}
        </p>
      )}
      {value.length > 0 ? (
        <div className='flex flex-row flex-wrap items-center gap-mili'>
          {value.map((fieldValue: any) => {
            return (
              fieldValue.length > 0 && (
                <Tag.Face key={fieldValue} className='bg-secondary-xlight'>
                  <Tag.Label className='text-secondary-medium text-sub'>
                    {fieldValue}
                  </Tag.Label>
                  <IoMdClose
                    onClick={() => {
                      if (!disabled) handleRemoveFieldValue(fieldValue)
                    }}
                    className='text-secondary-medium hover:cursor-pointer hover:text-secondary-dark'
                  />
                </Tag.Face>
              )
            )
          })}
        </div>
      ) : null}
    </>
  )
}
